import { useMemo, useState } from "react";
import { Search, Filter, Inbox, Sparkles } from "lucide-react";
import { HomeOSTaskCard } from "./HomeOSTaskCard";
import { householdMembers } from "../data/homeOsMockData";

/* ════════════════════════════════════════════════════════════════════════
   HomeOSTaskBoard — groups tasks by status into 5 ordered sections
   with simple filtering controls. All state is local & frontend only.
   ════════════════════════════════════════════════════════════════════════ */

const GROUP_ORDER = ["Needs action", "Ara suggested", "Upcoming", "Done", "Paused"];

const GROUP_META = {
  "Needs action": {
    accent: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",
    bg:     "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-amber-800/40",
    empty:  "Nothing needs action right now — nice work.",
  },
  "Ara suggested": {
    accent: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",
    bg:     "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-violet-800/40",
    empty:  "ARA hasn't suggested anything yet.",
  },
  "Upcoming": {
    accent: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",
    bg:     "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-blue-800/40",
    empty:  "Nothing scheduled — a calm patch.",
  },
  "Done": {
    accent: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",
    bg:     "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-emerald-800/40",
    empty:  "Nothing ticked off yet today.",
  },
  "Paused": {
    accent: "text-gray-500 dark:text-gray-400",
    bg:     "bg-gray-50 dark:bg-slate-800/40 border-gray-200 dark:border-slate-700",
    empty:  "Nothing paused.",
  },
};

const PRIORITIES = ["low", "medium", "high", "urgent"];

/* Resolve assignedTo id → friendly label for the filter dropdown */
function assigneeLabel(id) {
  if (id === "all") return "Everyone";
  if (id === "ara") return "ARA";
  const m = householdMembers.find(x => x.id === id);
  return m?.name || id;
}

export function HomeOSTaskBoard({ tasks, emptyHint, onEditTask, onDeleteTask, onMarkDone, onPause }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [person, setPerson] = useState("all");
  const [priority, setPriority] = useState("all");

  /* Build dropdown option lists from the task set itself so they only
     show real values (no dead options). */
  const categoryOptions = useMemo(
    () => Array.from(new Set(tasks.map(t => t.category))).sort(),
    [tasks]
  );
  const personOptions = useMemo(
    () => Array.from(new Set(tasks.map(t => t.assignedTo).filter(Boolean))),
    [tasks]
  );
  const priorityOptions = useMemo(
    () => PRIORITIES.filter(p => tasks.some(t => t.priority === p)),
    [tasks]
  );

  /* Filter pipeline */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter(t => {
      if (category !== "all" && t.category !== category) return false;
      if (person !== "all" && t.assignedTo !== person) return false;
      if (priority !== "all" && t.priority !== priority) return false;
      if (q) {
        const hay = [t.title, t.description, t.category, assigneeLabel(t.assignedTo)]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, search, category, person, priority]);

  /* Group by status, preserving the canonical ordering */
  const groups = useMemo(() => {
    const map = Object.fromEntries(GROUP_ORDER.map(s => [s, []]));
    for (const t of filtered) {
      if (map[t.status]) map[t.status].push(t);
      else if (map["Upcoming"]) map["Upcoming"].push(t); // fallback bucket
    }
    return map;
  }, [filtered]);

  const filtersActive = search || category !== "all" || person !== "all" || priority !== "all";

  return (
    <section data-testid="home-os-task-board" className="space-y-5">
      {/* Filters */}
      <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 p-3 sm:p-4">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.5} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks…"
              aria-label="Search tasks"
              className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 pl-9 pr-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-300 dark:focus:ring-violet-600"
              data-testid="task-board-search"
            />
          </div>

          {filtersActive && (
            <button
              onClick={() => { setSearch(""); setCategory("all"); setPerson("all"); setPriority("all"); }}
              className="text-xs font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:underline px-2 py-1"
              data-testid="task-board-clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="mt-3 space-y-2">
          <FilterPills
            label="Category" value={category} onChange={setCategory}
            options={categoryOptions} formatOption={(v) => v}
            testId="task-board-category"
          />
          <FilterPills
            label="Person" value={person} onChange={setPerson}
            options={personOptions} formatOption={assigneeLabel}
            testId="task-board-person"
          />
          <FilterPills
            label="Priority" value={priority} onChange={setPriority}
            options={priorityOptions} formatOption={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
            testId="task-board-priority"
          />
        </div>

        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-3 flex items-center gap-1">
          <Filter className="w-3 h-3" strokeWidth={1.5} />
          Showing <strong>{filtered.length}</strong> of {tasks.length} tasks.
        </p>
      </div>

      {/* Groups */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-900/40 p-10 text-center" data-testid="task-board-empty">
          <Inbox className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm font-bold text-gray-600 dark:text-gray-300">
            {emptyHint || "No tasks match those filters."}
          </p>
          <p className="text-xs text-gray-400 mt-1">Try clearing a filter or broadening your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="task-board-grid">
          {GROUP_ORDER.map(name => {
            const list = groups[name];
            const meta = GROUP_META[name];
            return (
              <TaskGroup
                key={name}
                name={name}
                meta={meta}
                tasks={list}
                onEditTask={onEditTask}
                onDeleteTask={onDeleteTask}
                onMarkDone={onMarkDone}
                onPause={onPause}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

function FilterPills({ label, value, onChange, options, formatOption, testId }) {
  if (options.length === 0) return null;
  return (
    <div className="flex items-start gap-2 flex-wrap" data-testid={`${testId}-row`}>
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-gray-500 pt-1.5 w-[60px] flex-shrink-0">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        <PillBtn
          active={value === "all"}
          onClick={() => onChange("all")}
          testId={`${testId}-all`}
        >
          All
        </PillBtn>
        {options.map(opt => (
          <PillBtn
            key={opt}
            active={value === opt}
            onClick={() => onChange(opt)}
            testId={`${testId}-${opt}`}
          >
            {formatOption(opt)}
          </PillBtn>
        ))}
      </div>
    </div>
  );
}

function PillBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold border transition-colors duration-150 ${
        active
          ? "bg-[#1a4d3e] text-white border-[#1a4d3e] dark:bg-[color:var(--ara-ink)] dark:border-emerald-600"
          : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:border-[color:var(--ara-line-soft)] dark:hover:border-violet-600"
      }`}
      data-testid={testId}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function TaskGroup({ name, meta, tasks, onEditTask, onDeleteTask, onMarkDone, onPause }) {
  return (
    <div
      className={`rounded-[20px] border ${meta.bg} p-3`}
      data-testid={`task-group-${name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <h3 className={`text-xs font-extrabold uppercase tracking-wider ${meta.accent}`}>
            {name}
          </h3>
          {name === "Ara suggested" && <Sparkles className="w-3 h-3 text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />}
        </div>
        <span className={`text-xs font-extrabold tabular-nums ${meta.accent}`}>
          {tasks.length}
        </span>
      </div>

      {tasks.length === 0 ? (
        <div className="text-[11px] text-gray-400 dark:text-gray-500 italic px-2 py-3 text-center">
          {meta.empty}
        </div>
      ) : (
        <div className="space-y-2.5">
          {tasks.map(t => (
            <HomeOSTaskCard
              key={t.id}
              task={t}
              onEdit={onEditTask}
              onDelete={onDeleteTask}
              onMarkDone={onMarkDone}
              onPause={onPause}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default HomeOSTaskBoard;
